import { Component, Input, EventEmitter, Output, OnInit, OnChanges, ViewChild, OnDestroy } from '@angular/core';
import { StepComponent } from '../../step.component';
import { PlanCoordinate } from '../../../services/plan-coordinate';
import { StrtPriority } from '../../../../shared/models/plan/StrtPriority';
import { StrtActivity } from '../../../../shared/models/plan/StrtActivity';
import { PlanStep } from '../../../services/PlanStep';
import { StrtInitiative } from '../../../../shared/models/plan/StrtInitiative';
import { SelectItem } from 'primeng/primeng';
import { AppLoadingService } from '../../../../app-loading-panel/app-loading.service';
import { IPriorityDropdownOptionValue, PriorityDropdownOption } from '../../../../shared/models/IPriorityDropdownOption';
import { PriorityDropdownComponent } from '../../../../shared/components/priority-dropdown/priority-dropdown.component';
import { PriorityService } from '../../../services/priority.service';
import { StrtObjective } from '../../../../shared/models/plan/StrtObjective';
import { Observable, Subscription } from 'rxjs/index';
import { InitiativeService } from '../../../services/initiative.service';
import { CommentExpansion } from '../../../../shared/models/ui/CommentExpansion';
import { take } from 'rxjs/internal/operators';
import { StrategiesDataStoreService } from '../../../../shared/services/strategies-data-store.service';
import { StrtHasFlags } from '../../../../shared/models/ui/StrtHasFlags';
import { ResourceService } from '../../../services/resource.service';

@Component({
  selector: 'app-enact-monitor-progress',
  templateUrl: './enact-monitor-progress.component.html',
  styleUrls: ['./enact-monitor-progress.component.scss']
})

export class EnactMonitorProgressComponent extends StepComponent<void> implements OnInit, OnDestroy, OnChanges {
  private readonly STATUS_COLOR_MAP = {
    'ON_TRACK': 'green',
    'LAGGING': 'yellow',
    'AT_RISK': 'red'
  };
  getPrioritiesCalled = false;

  unassignedStatusOptions: SelectItem[] = [
    {label: 'On Track', value: 'ON_TRACK'},
    {label: 'Lagging', value: 'LAGGING'},
    {label: 'At Risk', value: 'AT_RISK'}
  ];

  assignedStatusOptions: SelectItem[] = [
    {label: 'On Track', value: 'ON_TRACK'},
    {label: 'Lagging', value: 'LAGGING'},
    {label: 'At Risk', value: 'AT_RISK'},
    {label: 'Clear Status', value: null}
  ];


  filteredInitiativePriorities: StrtPriority[] = [];
  initiativePriorities: StrtPriority[];
  selectOptions: PriorityDropdownOption[] = [];
  selectedOptions: PriorityDropdownOption[] = [];
  isLoading$: Observable<boolean>;

  expandedCommentLocation: CommentExpansion;

  maxCommentLength = 150; // in chars

  private subscriptions: Subscription[] = [];

  hasFlags: StrtHasFlags = this._strtDataStore.defaultHasFlags;

  @Input() strategyId: number;
  @Output() getPriorities = new EventEmitter<{startDate: Date, endDate: Date}>();
  @Output() updateInitiativeStatus = new EventEmitter<StrtInitiative>();
  @Output() trackInitiative = new EventEmitter<StrtInitiative>();
  @ViewChild(PriorityDropdownComponent) priorityDropdown: PriorityDropdownComponent;


  constructor(private _loadingService: AppLoadingService,
              private _priorityService: PriorityService,
              private _strtDataStore: StrategiesDataStoreService,
              private _initiativeService: InitiativeService,
              _resourceService: ResourceService) {
    super(PlanCoordinate.stepBy(PlanStep.ENACT_AND_MONITOR_PROGRESS), _resourceService);
  }

  ngOnInit() {
    this.isLoading$ = this._loadingService.isLoading$;
    this.subscriptions.push(this._strtDataStore.datedInitiativesWithActivities$.subscribe(this.prioritiesHandler.bind(this)));
    this.subscriptions.push(this._strtDataStore.hasFlags$.subscribe(this.hasFlagsHandler.bind(this)));
    this.getPrioritiesCalled = false;
    this._priorityService.getPlanInitiativesWithActivities(this.strategyId);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
  }

  prioritiesHandler(newPriorities: StrtPriority[]) {
    this.priorities = newPriorities;
    this.setupDefaultView();
  }

  hasFlagsHandler(hasFlags: StrtHasFlags) {
    this.hasFlags = hasFlags;
  }

  ngOnChanges() {
  }

  setupDefaultView() {
    this.filteredInitiativePriorities = this.initiativePriorities;
    if (!this.getPrioritiesCalled) {
      this.getPrioritiesCalled = true;
      setTimeout(() => {
        this.priorityDropdown.selectAll();
      });
    }
  }

  @Input()
  set priorities(newPriorities: StrtPriority[]) {
    this.initiativePriorities = newPriorities.filter(
      priority => {
        const objectivesWithInitiatives = priority.objectives.filter(objective => objective.initiatives.length > 0);
        return objectivesWithInitiatives.length > 0;
      }
    );
    this.selectOptions = PriorityDropdownOption.fromPriorityInitiatives(this.initiativePriorities);
  }

  get priorities(): StrtPriority[] {
    return this.initiativePriorities;
  }

  get planHasPriorities() {
    return this.priorities.length > 0;
  }

  toggleInitiativeTracking(initiative: StrtInitiative) {
    initiative.trackingToolsEnabled = !initiative.trackingToolsEnabled;
    if (!initiative.trackingToolsEnabled) {
      initiative.initiativeStatus = null;
    }
    this.trackInitiative.emit(initiative);
  }

  getInitiativeStatusColor(initiative: StrtInitiative) {
    return this.STATUS_COLOR_MAP[initiative.initiativeStatus];
  }

  getInitiativeBorderColor(colorCode: string, index: number) {
    if (index === 0) {
      return colorCode;
    } else {
      return '';
    }
  }

  isLastActivity(activities: StrtActivity[], index: number) {
    return (activities.length - 1) === index;
  }

  addTargetsTitleBorder(activity: StrtActivity) {
    return activity.activityTargets.length === 0 && (activity.measure || '').length !== 0;
  }

  addMeasuresTitleBorder(activity: StrtActivity) {
    return activity.activityTargets.length !== 0 && (activity.measure || '').length === 0;
  }

  onSelectInitiativeStatus(event, initiative: StrtInitiative) {
    this.updateInitiativeStatus.emit(initiative);
  }

  dropdownValueChanged(event) {
    this.selectedOptions = event;
  }

  displaySelectedInitiatives() {
    // fetch latest selections from dropdown component.
    const selectedItems: IPriorityDropdownOptionValue[] = this.priorityDropdown.selectedOptions;

    if (selectedItems.length === 1 && selectedItems[0].primaryId === -1) {
      this.filteredInitiativePriorities = this.initiativePriorities;
    } else {
      this.filteredInitiativePriorities = [];
      const priorityIds: number[] = selectedItems.map((item: IPriorityDropdownOptionValue) => {
        return item.parentIds.priorityId;
      });
      const objectiveIds: number[] = selectedItems.map((item: IPriorityDropdownOptionValue) => {
        return item.parentIds.objectiveId;
      });
      const initiativeIds: number[] = selectedItems.map((item: IPriorityDropdownOptionValue) => {
        return item.primaryId;
      });

      let tempPriorities = JSON.parse(JSON.stringify(this.initiativePriorities));
      tempPriorities = tempPriorities.filter((priority: StrtPriority) => {
        if (priorityIds.indexOf(priority.priorityId) >= 0) {
          priority.objectives = priority.objectives.filter((objective: StrtObjective) => {
            if (objectiveIds.indexOf(objective.objectiveId) >= 0) {
              objective.initiatives = objective.initiatives.filter((initiative: StrtInitiative) => {
                return initiativeIds.indexOf(initiative.initiativeId) >= 0;
              });
              return true;
            }
            return false;
          });
          return true;
        }
        return false;
      });
      this.filteredInitiativePriorities = tempPriorities;
    }
  }

  /**
   * TODO: add response handling
   * @param {number} objectiveId
   * @param {number} initiativeId
   */
  deleteCommentForInitiative(objective: StrtObjective, initiative: StrtInitiative) {
    this._loadingService.setLoading();
    this._initiativeService.deleteStatusComment(this.strategyId, objective.objectiveId, initiative.initiativeId)
      .pipe(take(1))
      .subscribe((response: any) => {
        console.log(`Delete Comment Response`, response);
        initiative.comment = null;
        this._strtDataStore.updateInitiative(initiative, this.priorities);
        this._loadingService.clearLoading();
      },  (err) => {
        console.log(`Delete Comment Error `, err);
        this._loadingService.clearLoading();
      });
  }

  editCommentClicked(objective: StrtObjective, initiative: StrtInitiative) {
    this.expandedCommentLocation = {
      comment: initiative.comment,
      initiative: initiative,
      objective: objective,
      isCreate: false,
      isEdit: true,
      isDelete: false
    };
  }

  addCommentClicked(objective: StrtObjective, initiative: StrtInitiative) {
    this.expandedCommentLocation = {
      comment: null,
      initiative: initiative,
      objective: objective,
      isCreate: true,
      isEdit: false,
      isDelete: false
    };
  }

  cancelCommentAddEdit() {
    // Clear out expanded location and do not save changes
    this.expandedCommentLocation = null;
  }

  /**
   * Add response handling
   */
  editCommentForInitiative() {
    this._loadingService.setLoading();
    this._initiativeService.editStatusComment(this.strategyId,
      this.expandedCommentLocation.objective.objectiveId,
      this.expandedCommentLocation.initiative.initiativeId,
      this.expandedCommentLocation.comment)
      .pipe(take(1))
      .subscribe((response: any) => {
        console.log(`Edit Comment Response: `, response);
        const initiativeToUpdate: StrtInitiative = this.expandedCommentLocation.initiative;
        initiativeToUpdate.comment = this.expandedCommentLocation.comment;
        this._strtDataStore.updateInitiative(initiativeToUpdate, this.priorities);
        this.expandedCommentLocation = null;
        this._loadingService.clearLoading();
      }, (err) => {
        console.log(`Edit Comment Error: `, err);
        this._loadingService.clearLoading();
      });
  }

  /**
   * Add response handling
   */
  saveCommentForInitiative() {
    this._loadingService.setLoading();
    this._initiativeService.createStatusComment(this.strategyId,
      this.expandedCommentLocation.objective.objectiveId,
      this.expandedCommentLocation.initiative.initiativeId,
      this.expandedCommentLocation.comment)
      .pipe(take(1))
      .subscribe((response: any) => {
        console.log(`Create Comment Response: `, response);
        const initiativeToUpdate: StrtInitiative = this.expandedCommentLocation.initiative;
        initiativeToUpdate.comment = this.expandedCommentLocation.comment;
        this._strtDataStore.updateInitiative(initiativeToUpdate, this.priorities);
        this.expandedCommentLocation = null;
        this._loadingService.clearLoading();
      }, (err) => {
        console.log(`Create Comment Error: `, err);
        this._loadingService.clearLoading();
      });
  }

  commentRemainingChars() {
    return this.maxCommentLength - (this.expandedCommentLocation ?
      (this.expandedCommentLocation.comment ? this.expandedCommentLocation.comment.length : 0) : 0);
  }


}
